import { Grid, Stack } from "@mui/material";
import { LeadingActions, SwipeAction } from "react-swipeable-list";
import AfterSwipeContent from "../SwipeableMessage/AfterSwipeContent";
import { Reply } from "./Reply";
import { CommentType } from "./CommentTypes";
import {
    CommentActionsBox,
    CommentAvatar,
    CommentAvatarGrid,
    CommentContainerGrid,
    CommentContentContainer,
    CommentContentContainerGrid,
    CommentContentPaper,
    CommentDateTypography,
    CommentHeaderContainer,
    CommentHeaderLeftSide,
    CommentHeaderRightSide,
    CommentImageContainer,
    CommentOptionsButton,
    CommentReactionsBox,
    CommentReactionsGrid,
    CommentReactionsInnerGrid,
    CommentTextContentGrid,
    CommentTextTypography,
    CommentUserNameTypography,
    HighlightedBox,
    LinkoutButton,
    StyledSmall,
    StyledTextActionButton,
    SwipeableListStyled,
} from "./Comment.styles";
import { SwipeableCommentListItemStyled } from "../SwipeableMessage/AfterSwipeContent.style";
import React, { useMemo } from "react";
import { EllipsisHorizontalIcon } from "@icons/EllipsisHorizontalIcon";
import ImageGrid from "../ImageGrid/ImageGrid";
import userAvatarChecker from "../../Shared/UserAvatarChecker";
import { isUserCreator, UserType } from "../../Shared/types";
import { getUserName } from "../../Shared/UserHelpers";
import { MoreHorizontalIcon } from "@icons/MoreHorizontalIcon";
import { EntityReactionsListWithDefaultIcons } from "../../Shared/Components/Tippspiel/Reactions/EntityReactionsListWithDefaultIcons";
import { ReactionListPropsType } from "../../Shared/Components/Tippspiel/Reactions/ReactionsList.types";
import { UserRoleBadgeFactory } from "../UserRoleBadge/UserRoleBadgeFactory";
import { Box } from "@mui/system";
import { UserAvatarFactory } from "../UserAvatar/UserAvatarFactory";
import { TextExpander } from "../../Shared/TextExpander/TextExpander";
import getRelativeDate from "../RelativeDate/RelativeDate";
import { TEXT_ELLIPSIS } from "../../Shared/styles";
import { LinkoutButtonText } from "../Post/PostActions/PostActionButtons.styles";
import { ChevronRightIcon } from "@icons/ChevronRightIcon";
import { LinkSolidIcon } from "@icons/LinkSolidIcon";
import { useCommunityNavigation } from "@infrastructure/Hooks/useCommunityNavigation";
import { isOneEmoji } from "@infrastructure/Helpers/StringHelper";
import { ReportIconButton } from "../../Shared/Components/ReportIconButton";
import { useIsCurrentUserId } from "@infrastructure/Hooks/UserInteractions/useUserId";
import { StarIconLight } from "@icons/StarIconLight";
import { getUserReactionOnObject } from "@infrastructure/Hooks/UserInteractions/useUserReactions";
import { ReactionableObjectEnum } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";
import { TextMarkup } from "@shared/Components/TextMarkup/TextMarkup";

const TEXT_COMMENT_SUSPENDED = "Kommentar wurde entfernt.";
const TEXT_COMMENT_DELETED = "Kommentar wurde gelöscht.";

export function getCommentText(
    comment: unknown & {
        isDeleted?: boolean;
        isSuspended?: boolean;
    },
    defaultText?: () => string | undefined,
): string | undefined {
    switch (true) {
        case comment.isDeleted:
            return "*" + TEXT_COMMENT_DELETED + "*";
        case comment.isSuspended:
            return "*" + TEXT_COMMENT_SUSPENDED + "*";
    }

    return defaultText && defaultText();
}

export function Comment({
    comment,
    showCommentAvatarOptionsModal,
    handleSelectingComment,
    handleReplyComment,
    createReactOnCommentFunction,
    handleReportingComment,
    isPinnedComment = false,
    onRepliedCommentClick,
}: {
    comment: CommentType;
    handleReplyComment: (comment: CommentType) => void;
    handleReportingComment: (comment: CommentType) => void;
    handleSelectingComment: (comment: CommentType) => void;
    isPinnedComment: boolean;
    showCommentAvatarOptionsModal: (user: UserType) => void;
    createReactOnCommentFunction?: (
        commentId: string,
    ) => (reactionTypeName: string, action: string) => void;
    onRepliedCommentClick?: (id: number) => void;
}) {
    const commentContent = comment.commentContent;
    const user = commentContent.user;
    const commentActive = !(
        commentContent.isDeleted || commentContent.isSuspended
    );

    const { navigateTo } = useCommunityNavigation();

    const onClickComment = () =>
        commentActive && handleSelectingComment(comment);

    const leadingActions = () => (
        <LeadingActions>
            <SwipeAction onClick={onClickComment}>
                <AfterSwipeContent icon={<MoreHorizontalIcon />} />
            </SwipeAction>
        </LeadingActions>
    );

    const reactOnComment = createReactOnCommentFunction?.(
        comment.commentContent.id.toString(),
    );

    const reactionsListProps: ReactionListPropsType = {
        reactOnEntity: reactOnComment,
        customStyle: { backgroundColor: "transparent", padding: 0 },
        userReaction: getUserReactionOnObject(
            comment.id.toString(),
            ReactionableObjectEnum.COMMENT,
        ),
        reactions: comment.commentReactions,
    };

    const getUrlHost = (url: string) => {
        return new URL(url).hostname;
    };

    const commentText = useMemo(
        () =>
            getCommentText(commentContent, () => {
                return commentContent.text;
            }),
        [commentContent],
    );

    const isOwnComment = useIsCurrentUserId(user.uuid);
    return (
        <CommentContainerGrid direction="row">
            <CommentContentContainerGrid flex={1}>
                <SwipeableListStyled>
                    <SwipeableCommentListItemStyled
                        leadingActions={commentActive && leadingActions()}
                    >
                        <Box display={"flex"} width={"100%"}>
                            <CommentAvatarGrid>
                                {commentContent?.showAvatar &&
                                    user &&
                                    UserAvatarFactory({
                                        avatar: user.avatar,
                                        uuid: user.uuid,
                                        gender: user.gender,
                                        onClick: () =>
                                            showCommentAvatarOptionsModal(
                                                commentContent.user,
                                            ),
                                        onError: () =>
                                            userAvatarChecker(
                                                user.avatar,
                                                user.uuid,
                                            ),
                                        component: CommentAvatar,
                                        sx: {
                                            cursor: "pointer",
                                        },
                                    })}
                            </CommentAvatarGrid>
                            <CommentContentContainer
                                sx={{
                                    maxWidth: comment.commentReply
                                        ? "calc(100% - 44px)"
                                        : "100%",
                                }}
                            >
                                <CommentContentPaper
                                    sx={{
                                        backgroundColor: isPinnedComment
                                            ? "var(--community-color-transparent-light-steal-blue-20)"
                                            : commentContent.isHighlighted
                                            ? "var(--community-color-transparent-light-steal-blue-10)"
                                            : "var(--community-color-transparent-black-05)",
                                    }}
                                >
                                    <Grid container>
                                        <Grid item xs>
                                            <CommentHeaderContainer>
                                                <CommentHeaderLeftSide>
                                                    <Box
                                                        onClick={() => {
                                                            showCommentAvatarOptionsModal(
                                                                commentContent.user,
                                                            );
                                                        }}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                margin: "0",
                                                            }}
                                                        >
                                                            <CommentUserNameTypography>
                                                                {getUserName(
                                                                    user.screenName,
                                                                )}
                                                            </CommentUserNameTypography>
                                                        </Box>
                                                        <UserRoleBadgeFactory
                                                            roleBadge={
                                                                user.role
                                                            }
                                                        />
                                                    </Box>
                                                    <Box lineHeight={1}>
                                                        <CommentDateTypography>
                                                            {getRelativeDate(
                                                                commentContent.date,
                                                            )}
                                                        </CommentDateTypography>
                                                        {commentContent.isEdited &&
                                                            !isUserCreator(
                                                                commentContent
                                                                    .user.role,
                                                            ) && (
                                                                <StyledSmall>
                                                                    {" "}
                                                                    (editiert)
                                                                </StyledSmall>
                                                            )}
                                                    </Box>
                                                </CommentHeaderLeftSide>
                                                <CommentHeaderRightSide>
                                                    {commentActive && (
                                                        <CommentOptionsButton
                                                            onClick={
                                                                onClickComment
                                                            }
                                                            aria-label="settings"
                                                        >
                                                            <EllipsisHorizontalIcon />
                                                        </CommentOptionsButton>
                                                    )}
                                                    {commentContent.isHighlighted && (
                                                        <HighlightedBox>
                                                            <StarIconLight
                                                                color="var(--community-color-snow-white)"
                                                                width="12px"
                                                                height="12px"
                                                            />
                                                        </HighlightedBox>
                                                    )}
                                                </CommentHeaderRightSide>
                                            </CommentHeaderContainer>
                                        </Grid>
                                    </Grid>
                                    <Grid container wrap="nowrap">
                                        <CommentTextContentGrid item>
                                            {comment.commentReply && (
                                                <Reply
                                                    {...comment.commentReply}
                                                    onClick={
                                                        onRepliedCommentClick
                                                    }
                                                />
                                            )}

                                            {commentContent.images.length >
                                                0 && (
                                                <CommentImageContainer
                                                    isOneImage={
                                                        commentContent.images
                                                            .length === 1
                                                    }
                                                >
                                                    <ImageGrid
                                                        images={commentContent.images.map(
                                                            (item) => ({
                                                                url: item.url,
                                                            }),
                                                        )}
                                                    />
                                                </CommentImageContainer>
                                            )}
                                            {!!commentText?.length && (
                                                <CommentTextTypography
                                                    showBigText={
                                                        commentContent.text
                                                            ? isOneEmoji(
                                                                  commentContent.text,
                                                              )
                                                            : false
                                                    }
                                                >
                                                    <TextExpander
                                                        lineClampValue={10}
                                                    >
                                                        <TextMarkup
                                                            textToMarkup={
                                                                commentText
                                                            }
                                                        />
                                                    </TextExpander>
                                                </CommentTextTypography>
                                            )}
                                        </CommentTextContentGrid>
                                    </Grid>
                                    {commentContent.linkoutUrl &&
                                        commentActive && (
                                            <LinkoutButton
                                                disableRipple
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigateTo(
                                                        commentContent.linkoutUrl,
                                                    );
                                                }}
                                                endIcon={<ChevronRightIcon />}
                                            >
                                                <Stack
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    sx={{
                                                        width: "40px",
                                                    }}
                                                >
                                                    <LinkSolidIcon
                                                        color="var(--community-color-light-steal-blue)"
                                                        width="16px"
                                                        height="16px"
                                                    />
                                                </Stack>
                                                <Stack
                                                    sx={{
                                                        minWidth: 0,
                                                        ...TEXT_ELLIPSIS,
                                                    }}
                                                    flex="1"
                                                    alignItems="flex-start"
                                                >
                                                    <LinkoutButtonText>
                                                        {getUrlHost(
                                                            commentContent.linkoutUrl,
                                                        )}
                                                    </LinkoutButtonText>
                                                </Stack>
                                            </LinkoutButton>
                                        )}
                                </CommentContentPaper>
                                {commentActive && (
                                    <Stack
                                        justifyContent={"space-between"}
                                        direction={"row"}
                                        alignItems={"center"}
                                    >
                                        <CommentReactionsBox>
                                            <CommentActionsBox>
                                                <CommentReactionsGrid container>
                                                    <CommentReactionsInnerGrid
                                                        item
                                                    >
                                                        <EntityReactionsListWithDefaultIcons
                                                            {...reactionsListProps}
                                                        />
                                                    </CommentReactionsInnerGrid>
                                                </CommentReactionsGrid>
                                            </CommentActionsBox>
                                            {!commentContent.isEditorial && (
                                                <StyledTextActionButton
                                                    disableFocusRipple
                                                    disableTouchRipple
                                                    onClick={() =>
                                                        handleReplyComment(
                                                            comment,
                                                        )
                                                    }
                                                >
                                                    Antworten
                                                </StyledTextActionButton>
                                            )}
                                        </CommentReactionsBox>
                                        {!isOwnComment &&
                                            !commentContent.isEditorial && (
                                                <ReportIconButton
                                                    onReportButtonClick={() => {
                                                        handleReportingComment(
                                                            comment,
                                                        );
                                                    }}
                                                />
                                            )}
                                    </Stack>
                                )}
                            </CommentContentContainer>
                        </Box>
                    </SwipeableCommentListItemStyled>
                </SwipeableListStyled>
            </CommentContentContainerGrid>
        </CommentContainerGrid>
    );
}
